// @owners { team: patients-team }
import { useNavigation } from '@alto/navigation';
import React, { useEffect } from 'react';
// eslint-disable-next-line import/no-deprecated
import { clearInsuranceError, declineInsurance } from '~shared/actions/insurances';
// eslint-disable-next-line import/no-deprecated
import { updateOnboardingUser } from '~shared/actions/onboarding';
import {
  CREATE_ACCOUNT,
  INSURANCE_FOUND,
  INSURANCE_INPUT,
  INSURANCE_INPUT_MANUAL,
  INSURANCE_INPUT_PHOTO,
  ONBOARD_STATE_INSURANCE_INPUT,
} from '~shared/features/onboarding/helpers/onboardingRouteHandler';
import { routeFromPath } from '~shared/features/onboarding/helpers/routeToPath';
import { getEditingOnboardingInsuranceID } from '~shared/features/onboarding/selectors/getOnboarding';
import getShowProgynySelfServiceForUser from '~shared/features/users/selectors/getShowProgynySelfServiceForUser';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useGetInsurances } from '~shared/queries/query-keys/insurances';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { OnboardingInsuranceInput } from './OnboardingInsuranceInput';
import { useNavigationContext } from '~web/contexts';
import { nextUrl } from '~web/features/onboarding/helpers/urlHandler';

export const OnboardingInsuranceInputContainer = () => {
  const { navigate } = useNavigation();
  const dispatch = useDispatchShared();
  const { trackEvent } = useAnalytics();
  const showProgyny = useSelectorShared(getShowProgynySelfServiceForUser);
  const { previous: previousUrl } = useNavigationContext();
  const origin = previousUrl ? routeFromPath(previousUrl) : '';
  const editingInsuranceID = useSelectorShared(getEditingOnboardingInsuranceID);
  const { hasInsurance } = useGetInsurances();
  const photoURL = nextUrl({
    params: { editingInsuranceID },
    currentRoute: INSURANCE_INPUT,
    desiredRoute: INSURANCE_INPUT_PHOTO,
  });
  const manualURL = nextUrl({
    params: { editingInsuranceID },
    currentRoute: INSURANCE_INPUT,
    desiredRoute: INSURANCE_INPUT_MANUAL,
  });
  const skipURL = nextUrl({
    params: { editingInsuranceID },
    currentRoute: INSURANCE_INPUT,
    desiredRoute: hasInsurance ? INSURANCE_FOUND : CREATE_ACCOUNT,
  });

  const createAccountURL = nextUrl({
    params: { editingInsuranceID },
    currentRoute: INSURANCE_INPUT,
    desiredRoute: CREATE_ACCOUNT,
  });

  useEffect(() => {
    dispatch(
      // eslint-disable-next-line import/no-deprecated
      updateOnboardingUser({
        onboard_state: ONBOARD_STATE_INSURANCE_INPUT,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearInsuranceError());
    };
  }, [dispatch]);

  const onUploadPhoto = () => {
    navigate(photoURL);
  };

  const onEnterManually = () => {
    navigate(manualURL);
  };

  const onDeclineInsurance = async () => {
    trackEvent({
      event: EVENTS.ONBOARDING__INSURANCE_NONE_CLICKED,
      params: {
        is_progyny_rx_view: showProgyny,
      },
    });

    // eslint-disable-next-line import/no-deprecated
    const success = await dispatch(declineInsurance());
    if (success) {
      navigate(createAccountURL);
    }
  };

  const onSkip = () => {
    trackEvent({
      event: EVENTS.ONBOARDING__INSURANCE_REMINDER_CLICKED,
      params: {
        is_progyny_rx_view: showProgyny,
      },
    });

    navigate(skipURL);
  };

  return (
    <OnboardingInsuranceInput
      origin={origin}
      showProgyny={showProgyny}
      onUploadPhoto={onUploadPhoto}
      onEnterManually={onEnterManually}
      onDeclineInsurance={onDeclineInsurance}
      onSkip={onSkip}
    />
  );
};
